import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { FaSearch, FaTimes } from "react-icons/fa";

/**
 * SearchInput — مكوّن بحث موحّد للمشروع
 *
 * Props:
 *  value        {string}   — قيمة الحقل الحالية
 *  onChange     {fn}       — (newValue: string) => void
 *  placeholder  {string}   — نص placeholder
 *  isLoading    {boolean}  — يُظهر مؤشر تحميل داخل الحقل
 *  isRTL        {boolean}  — اتجاه النص (عربي/إنجليزي)
 *  style        {object}   — styles إضافية للـ wrapper
 *  inputStyle   {object}   — styles إضافية للـ input
 */
const SearchInput = ({
  value = "",
  onChange,
  placeholder,
  isLoading = false,
  isRTL = true,
  style = {},
  inputStyle = {},
}) => {
  const { t } = useTranslation("global");
  const inputRef = useRef(null);

  const handleClear = () => {
    onChange("");
    inputRef.current?.focus();
  };

  const iconSide = isRTL ? "right" : "left";
  const clearSide = isRTL ? "left" : "right";
  const paddingKey = isRTL ? "paddingRight" : "paddingLeft";
  const paddingOppKey = isRTL ? "paddingLeft" : "paddingRight";

  return (
    <div
      style={{
        position: "relative",
        flex: 1,
        minWidth: "200px",
        ...style,
      }}
    >
      {/* Search / Loading icon */}
      <span
        style={{
          position: "absolute",
          [iconSide]: "13px",
          top: "50%",
          transform: "translateY(-50%)",
          color: "#666",
          fontSize: "14px",
          pointerEvents: "none",
          display: "flex",
          alignItems: "center",
        }}
      >
        {isLoading ? <Spinner /> : <FaSearch />}
      </span>

      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || t("common.search")}
        style={{
          width: "100%",
          [paddingKey]: "40px",
          [paddingOppKey]: value ? "36px" : "14px",
          paddingTop: "10px",
          paddingBottom: "10px",
          backgroundColor: "#1b3350",
          border: "1px solid #333",
          borderRadius: "8px",
          color: "white",
          fontSize: "14px",
          textAlign: isRTL ? "right" : "left",
          direction: isRTL ? "rtl" : "ltr",
          boxSizing: "border-box",
          transition: "border-color 0.2s",
          outline: "none",
          ...inputStyle,
        }}
        onFocus={e => {
          e.target.style.borderColor = "#AC2000";
        }}
        onBlur={e => {
          e.target.style.borderColor = "#333";
        }}
      />

      {/* Clear button — يظهر فقط لما فيه نص */}
      {value && (
        <button
          onClick={handleClear}
          title={t("search.clear")}
          style={{
            position: "absolute",
            [clearSide]: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            background: "none",
            border: "none",
            color: "#888",
            cursor: "pointer",
            padding: "4px",
            display: "flex",
            alignItems: "center",
            borderRadius: "50%",
            transition: "color 0.2s, background 0.2s",
          }}
          onMouseEnter={e => {
            e.currentTarget.style.color = "#fff";
            e.currentTarget.style.background = "rgba(172,32,0,0.3)";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.color = "#888";
            e.currentTarget.style.background = "none";
          }}
        >
          <FaTimes size={12} />
        </button>
      )}
    </div>
  );
};

/* ── مؤشر التحميل ── */
const Spinner = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#AC2000"
    strokeWidth="2.5"
    strokeLinecap="round"
    style={{ animation: "searchSpin 0.8s linear infinite" }}
  >
    <style>{`@keyframes searchSpin { to { transform: rotate(360deg); } }`}</style>
    <path d="M12 2a10 10 0 0 1 10 10" />
  </svg>
);

export default SearchInput;
